import { Injectable } from '@angular/core';
import { ListFormat } from '@app/models';

type boolNumber = 0 | 1;

export interface UrlParams {
  loginRequired: boolNumber;

  tab: boolNumber;
  sidebar: boolNumber;

  configPersonal: boolNumber;
  configSecurity: boolNumber;
  configAppearance: boolNumber;
  configNotifications: boolNumber;
  configGeneral: boolNumber;
  configPlans: boolNumber;
  configEmails: boolNumber;
  configMembers: boolNumber;
  configGroups: boolNumber;
  configWebhooks: boolNumber;
  configCustomization: boolNumber;
  configApi: boolNumber;
  configReferral: boolNumber;

  folderBreadCrumbs: boolNumber;
  documentationLink: boolNumber;
  mobileNavigation: boolNumber;
  menuBackButton: boolNumber;
  archiveDocument: boolNumber;

  listType: ListFormat;
  mainPath: string;
  configPath: string;
  language: string;
  timezone: string;
}

@Injectable({ providedIn: 'root' })
export class UrlParamsService {
  private _params: UrlParams;
  private _dirtyParams: Set<keyof UrlParams> = new Set();
  private _boolParams: (keyof UrlParams)[] = [
    'loginRequired',

    'tab',
    'sidebar',

    'configPersonal',
    'configSecurity',
    'configAppearance',
    'configNotifications',
    'configGeneral',
    'configPlans',
    'configEmails',
    'configMembers',
    'configGroups',
    'configWebhooks',
    'configCustomization',
    'configApi',
    'configReferral',

    'folderBreadCrumbs',
    'documentationLink',
    'menuBackButton',
    'mobileNavigation',
    'archiveDocument'
  ];

  constructor() {
    this._params = {
      ...(Object.fromEntries(this._boolParams.map(attr => [attr, 1])) as Partial<UrlParams>), // Sets default for every boolean attribute as 1
      loginRequired: 0, // Unlike the others, default here is 0

      listType: ListFormat.Box,
      mainPath: '',
      configPath: '',
      language: '',
      timezone: ''
    } as UrlParams;
  }

  get params(): UrlParams {
    return this._params;
  }

  set params(values: UrlParams) {
    if (!values) {
      return;
    }

    for (const rawKey in values) {
      const key = rawKey as keyof UrlParams;
      if (!this._params.hasOwnProperty(key)) {
        continue;
      }

      if (this._boolParams.includes(key) && ['0', '1', 0, 1].includes(values[key] as string)) {
        // @ts-expect-error: The if is making sure the parameter exists
        this._params[key] = parseInt(values[key]);
        this._dirtyParams.add(key);
      } else if (!this._boolParams.includes(key)) {
        // @ts-expect-error: The first if with hasOwnProperty is making sure the key exists
        this._params[key] = String(values[key]);
        this._dirtyParams.add(key);
      }
    }
  }

  get dirtyParams(): Set<keyof UrlParams> {
    return this._dirtyParams;
  }

  isParamDirty(param: keyof UrlParams): boolean {
    return this._dirtyParams.has(param);
  }

  isConfigOrganizationActive(): boolean {
    const attrs: (keyof UrlParams)[] = ['configGeneral', 'configPlans', 'configEmails', 'configMembers', 'configGroups', 'configWebhooks'];
    return attrs.findIndex(attr => !!this._params[attr]) >= 0;
  }

  isConfigDeveloperActive(): boolean {
    return !!this._params.configApi;
  }

  isConfigOtherActive(): boolean {
    return !!this._params.configReferral;
  }
}
